import { Option } from '@cmg/common';
import { AsyncAutocomplete, InputAdornment, Popper, SearchIcon, styled } from '@cmg/design-system';
import { companySearchTestId } from '@cmg/e2e-selectors';
import React from 'react';

import { SSearchWrapper } from './CompanySearch.styles';
import CompanySearchOption from './CompanySearchOption';
import type { Company, CompanyOption } from './types';

type Props = {
  onItemClick: (companyId: Company['id']) => void;
  onSearchOptions: (searchTerm: string) => Promise<CompanyOption[]>;
  autoFocus?: boolean;
};

const StyledPopper = styled(Popper)({
  '& .MuiAutocomplete-paper': {
    minWidth: '525px', // Unique case - dropdown can be wider than search box to support large text snippets
  },
});

/**
 * Company search input handles fetching and displaying search results
 */
export const CompanySearch: React.FC<Props> = ({ onItemClick, onSearchOptions, autoFocus }) => {
  const [loading, setLoading] = React.useState(false);

  const handleFetchOptions = React.useCallback(
    async (inputValue: string) => {
      if (inputValue.length === 0) {
        return { data: [], pagination: { hasNext: false } };
      }

      setLoading(true);
      const companyOptions = await onSearchOptions(inputValue);
      setLoading(false);
      return {
        data: companyOptions,
        pagination: {
          hasNext: false,
        },
      };
    },
    [onSearchOptions, setLoading]
  );

  const handleOnChange = React.useCallback(
    (
      event: React.SyntheticEvent,
      value: NonNullable<string | CompanyOption> | (string | CompanyOption)[] | null,
      reason: string
    ) => {
      if (
        reason === 'selectOption' &&
        value &&
        !Array.isArray(value) &&
        (value as CompanyOption).value
      ) {
        onItemClick((value as CompanyOption).value); // Trigger the action with the selected company's ID
      }
    },
    [onItemClick]
  );

  const renderOption = (option: Option<Company['id']>, inputValue: string) => {
    return <CompanySearchOption option={option as CompanyOption} searchInput={inputValue} />;
  };

  return (
    <SSearchWrapper
      data-test-id={companySearchTestId.testId}
      data-testid={companySearchTestId.testId}
    >
      <AsyncAutocomplete
        isLoadingOptions={loading}
        autoFocus={autoFocus}
        freeSolo
        noOptionsText="Please start typing a company name or ticker to see results"
        showNoOptionsWhenNoInput={false}
        onFetchOptions={handleFetchOptions}
        onChange={handleOnChange}
        renderOption={(props, option, { inputValue }) => (
          <li {...props}>{renderOption(option, inputValue)}</li>
        )}
        TextFieldProps={{
          placeholder: 'Search for companies...',
          size: 'small',
          InputProps: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            // @ts-expect-error MUI has a bad type here
            endAdornment: null, // Hides the dropdown arrow
          },
        }}
        PopperComponent={props => <StyledPopper {...props} placement="bottom-start" />}
      />
    </SSearchWrapper>
  );
};

export default CompanySearch;
